// Phase 0085 — Awards / Best Performer page.

import { useState } from 'react'
import { App, Badge, Button, Card, Col, DatePicker, Empty, Row, Space, Table, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import dayjs, { type Dayjs } from 'dayjs'
import { useMutation, useQuery } from '@tanstack/react-query'

import { awardApi, scoringApi, type PerformanceAward, type MonthlyScore } from '@/features/hr/taskExecutionApi'
import PageHeader from '@/shared/components/PageHeader'

const { Text, Title } = Typography

const AWARD_LABELS: Record<string, string> = {
  BEST_OPERATOR: 'Best Operator',
  BEST_SUPERVISOR: 'Best Supervisor',
  BEST_MAINTENANCE: 'Best Maintenance',
  BEST_QC: 'Best QC',
  BEST_NEWCOMER: 'Best Newcomer',
  BEST_DEPARTMENT: 'Best Department',
}

const AWARD_COLORS: Record<string, string> = {
  BEST_OPERATOR: '#fadb14',
  BEST_SUPERVISOR: '#13c2c2',
  BEST_MAINTENANCE: '#722ed1',
  BEST_QC: '#52c41a',
  BEST_NEWCOMER: '#eb2f96',
  BEST_DEPARTMENT: '#fa8c16',
}

export default function AwardsPage() {
  const { message } = App.useApp()
  const [month, setMonth] = useState<Dayjs>(dayjs().startOf('month'))

  const year = month.year()
  const m = month.month() + 1

  const { data: awards = [], isLoading, refetch } = useQuery({
    queryKey: ['awards', year, m],
    queryFn: () => awardApi.list(year, m),
  })

  const { data: leaderboard = [] } = useQuery({
    queryKey: ['leaderboard', year, m],
    queryFn: () => scoringApi.leaderboard(year, m, 10),
  })

  const generateMut = useMutation({
    mutationFn: () => awardApi.generate(year, m),
    onSuccess: (data) => {
      message.success(`Generated ${data.total_generated} awards`)
      refetch()
    },
    onError: (e: Error) => message.error(e.message),
  })

  const computeMut = useMutation({
    mutationFn: () => scoringApi.computeMonthly(year, m),
    onSuccess: () => {
      message.success('Monthly scores recomputed')
      refetch()
    },
    onError: (e: Error) => message.error(e.message),
  })

  const publishMut = useMutation({
    mutationFn: (id: string) => awardApi.publish(id),
    onSuccess: () => { message.success('Published'); refetch() },
    onError: (e: Error) => message.error(e.message),
  })

  const columns: ColumnsType<MonthlyScore> = [
    { title: '#', dataIndex: 'rank_overall', width: 50,
      render: (r: number | null) => r ? <Tag color={r === 1 ? 'gold' : r <= 3 ? 'orange' : 'default'}>{r}</Tag> : '—' },
    { title: 'Employee', dataIndex: 'employee_id', width: 260,
      render: (id: string) => <Text code style={{ fontSize: 11 }}>{id}</Text> },
    { title: 'Total Score', dataIndex: 'total_score', width: 110,
      render: (v: number) => <Text strong>{Number(v).toFixed(1)}</Text> },
    { title: 'Completion %', dataIndex: 'completion_pct', width: 110,
      render: (v: number) => `${Number(v).toFixed(1)}%` },
    { title: 'Tasks Done', dataIndex: 'tasks_completed', width: 100 },
    { title: 'Overdue', dataIndex: 'tasks_overdue', width: 80 },
  ]

  return (
    <>
      <PageHeader
        title="Performance Awards"
        subtitle="Monthly recognition for best operator, supervisor, maintenance, QC, newcomer, department"
        extra={
          <Space>
            <DatePicker.MonthPicker value={month} onChange={(d) => d && setMonth(d)} />
            <Button onClick={() => computeMut.mutate()} loading={computeMut.isPending}>
              Recompute Monthly Scores
            </Button>
            <Button type="primary" onClick={() => generateMut.mutate()} loading={generateMut.isPending}>
              Generate Awards
            </Button>
          </Space>
        }
      />

      <Title level={5}>Awards — {month.format('MMMM YYYY')}</Title>
      {awards.length === 0 ? (
        <Empty description="No awards yet. Click 'Generate Awards' after monthly scores are computed." />
      ) : (
        <Row gutter={[12, 12]} style={{ marginBottom: 24 }}>
          {awards.map((a) => (
            <Col key={a.id} xs={24} md={12} lg={8}>
              <Card
                title={
                  <Space>
                    <Badge color={AWARD_COLORS[a.award_type] ?? '#1677ff'} />
                    {AWARD_LABELS[a.award_type] || a.award_type}
                  </Space>
                }
                extra={
                  a.is_published ? <Tag color="green">Published</Tag> :
                    <Button size="small" onClick={() => publishMut.mutate(a.id)}>Publish</Button>
                }
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Text style={{ fontSize: 18, fontWeight: 600 }}>
                    Score: {Number(a.score_value).toFixed(1)}
                  </Text>
                  {a.citation && <Text>{a.citation}</Text>}
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {a.employee_id ? `Employee: ${a.employee_id}` : ''}
                    {a.department_id ? `Department: ${a.department_id}` : ''}
                  </Text>
                </Space>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      <Title level={5}>Top 10 — Leaderboard</Title>
      <Table rowKey="id" columns={columns} dataSource={leaderboard}
             pagination={false} size="small" />
    </>
  )
}
